import React, { useEffect, useRef } from "react";

const OtpInput = ({
  length = 6,
  value = "",
  onChange = () => {},
  disabled = false,
  autoFocus = true,
}) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const filledCount = value.length;
    const targetIndex = filledCount >= length ? length - 1 : filledCount;
    inputRefs.current[targetIndex]?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getOtpArray = () =>
    Array.from({ length }, (_, i) => (value[i] ? value[i] : ""));

  const focusInput = (index) => {
    if (index >= 0 && index < length) {
      inputRefs.current[index]?.focus();
    }
  };

  const setDigit = (index, digit) => {
    const arr = getOtpArray();
    arr[index] = digit;
    onChange(arr.join(""));
  };

  const handleChange = (e, index) => {
    if (disabled) return;
    const digit = e.target.value.replace(/\D/g, "").slice(-1);
    if (!digit) {
      setDigit(index, "");
      return;
    }
    setDigit(index, digit);
    if (index < length - 1) focusInput(index + 1);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[index]) {
        setDigit(index, "");
      } else if (index > 0) {
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    if (disabled) return;
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasted) return;

    const arr = Array.from({ length }, () => "");
    pasted.split("").forEach((digit, i) => {
      if (index + i < length) arr[index + i] = digit;
    });
    onChange(arr.join(""));

    const lastFilled = Math.min(index + pasted.length - 1, length - 1);
    focusInput(lastFilled + 1 < length ? lastFilled + 1 : lastFilled);
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div
      role="group"
      aria-label="6-digit OTP code"
      className="flex w-fit items-center gap-[10px]"
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete="one-time-code"
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${length}`}
          value={getOtpArray()[index]}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={handleFocus}
          className={`h-8 w-8 rounded-xl border border-gray-300 bg-white text-center text-2xl font-bold text-gray-800 shadow-sm outline-none transition-all duration-200 hover:border-blue-300 focus:scale-102 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 sm:h-12 sm:w-12 ${
            value[index] ? "border-blue-400" : ""
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
